import React from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut, ChevronDown, PanelLeftClose, PanelLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NAVIGATION, DASHBOARD_NAME } from "@/config/navigation";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationsBell from "@/components/NotificationsBell";
import VsAI from "@/components/VsAI";
import { StatusBadge } from "@/components/StatusBadge";
import { VsMark } from "@/components/Logo";
import { useSEO } from "@/components/Seo";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

function SidebarContent({ role, onNavigate }) {
  const groups = NAVIGATION[role] || [];
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 h-16 flex items-center gap-2.5 border-b border-white/10 shrink-0">
        <VsMark className="h-8 w-8" tile />
        <div><div className="font-display font-bold text-white text-sm leading-tight">VS PartnerHub</div>
          <div className="text-[10px] text-blue-300">{DASHBOARD_NAME[role]}</div></div>
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
        {groups.map((g) => (
          <div key={g.title}>
            <div className="px-3 text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{g.title}</div>
            <div className="space-y-0.5">
              {g.items.map((it) => (
                <NavLink key={it.to} to={it.to} onClick={onNavigate} end
                  data-testid={`nav-${it.to.split("/").pop()}`}
                  className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-vs-royal text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
                  <it.icon className="h-4 w-4 shrink-0" /><span className="truncate">{it.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, organization, logout } = useAuth();
  // Private application area — never indexable (defense in depth with robots.txt + auth).
  useSEO({ title: "VS PartnerHub — Secure Workspace", path: "/app", robots: "noindex,nofollow" });
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-pulse text-slate-400">Loading workspace…</div></div>;
  if (!user) { nav("/login"); return null; }

  const initials = (user.name || "U").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-vs-deep shrink-0 fixed inset-y-0 left-0 z-30">
        <SidebarContent role={user.role} />
      </aside>

      {/* Mobile sidebar */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden" onClick={() => setMobileOpen(false)} />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }} transition={{ type: "spring", damping: 26 }} className="fixed inset-y-0 left-0 z-50 w-64 bg-vs-deep lg:hidden">
              <SidebarContent role={user.role} onNavigate={() => setMobileOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200">
          <div className="h-16 px-4 sm:px-6 flex items-center gap-3">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setMobileOpen(true)} data-testid="mobile-sidebar-toggle"><Menu className="h-5 w-5" /></button>
            <div className="flex-1 max-w-md hidden sm:block"><GlobalSearch /></div>
            <div className="flex-1 sm:hidden" />
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50" data-testid="user-menu-trigger">
                  <div className="h-9 w-9 rounded-full vs-gradient flex items-center justify-center text-white text-xs font-semibold">{initials}</div>
                  <div className="hidden sm:block text-left"><div className="text-sm font-semibold text-slate-800 leading-tight">{user.name}</div>
                    <div className="text-[11px] text-slate-500">{organization?.name || DASHBOARD_NAME[user.role]}</div></div>
                  <ChevronDown className="h-4 w-4 text-slate-400 hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 bg-white">
                <div className="px-3 py-2.5">
                  <div className="text-sm font-semibold text-slate-800">{user.name}</div>
                  <div className="text-xs text-slate-500">{user.email}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <StatusBadge status={user.role === "super_admin" ? "active" : organization?.verification_status} />
                  </div>
                </div>
                <DropdownMenuSeparator />
                {user.role !== "super_admin" && <DropdownMenuItem onClick={() => nav("/app/profile")} data-testid="menu-profile">Company Profile</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => nav("/app/support")} data-testid="menu-support">Help & Support</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} className="text-rose-600" data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </main>
      </div>
      <VsAI />
    </div>
  );
}
